/*
  - Front-end GWP eligibility checks mirroring the Shopify Function logic.
  - Dispatches 'tarte:gwp-eligibility-updated' whenever cart state changes.
  - Auto-adds/removes the autoAdd GWP variant based on eligibility.
*/

const GWP_DISMISSED_KEY = 'gwp_auto_add_dismissed';

/**
 * Reads the set of auto-add GWP variant IDs the customer has manually dismissed
 * this session from sessionStorage.
 *
 * @returns {Set<string>} Numeric variant IDs the customer has dismissed.
 */
function getDismissedAutoAddVariants() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(GWP_DISMISSED_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

/**
 * Persists the current dismissed variant set to the Shopify cart attribute
 * `_gwp_auto_add_dismissed` so it survives page reloads and is readable in
 * checkout extensions where sessionStorage is unavailable.
 *
 * @param {Set<string>} dismissedSet - Numeric variant IDs to persist.
 * @returns {void}
 */
function persistDismissedCartAttribute(dismissedSet) {
  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: { _gwp_auto_add_dismissed: JSON.stringify([...dismissedSet]) },
    }),
  }).catch(() => {});
}

/**
 * Marks a variant as dismissed by the customer so it is not re-added automatically.
 * Writes to both sessionStorage (fast, same-tab) and the cart attribute (cross-tab
 * and cross-reload persistence).
 *
 * @param {string|number} variantId - Numeric variant ID to dismiss.
 * @returns {void}
 */
function dismissAutoAddVariant(variantId) {
  try {
    const dismissed = getDismissedAutoAddVariants();
    dismissed.add(String(variantId));
    sessionStorage.setItem(GWP_DISMISSED_KEY, JSON.stringify([...dismissed]));
    persistDismissedCartAttribute(dismissed);
  } catch {
    console.log("[custom-promotions] sessionStorage not available")
  }
}

/**
 * Extracts the numeric ID from a Shopify GID string.
 *
 * @param {string} gid - A Shopify GID, e.g. `gid://shopify/ProductVariant/123`.
 * @returns {string} The numeric portion, e.g. `'123'`. Returns `''` if falsy input.
 */
function extractNumericId(gid) {
  if (!gid) return '';
  const parts = String(gid).split('/');
  return parts[parts.length - 1];
}

/**
 * Returns the current storefront country code from `window.Shopify.country`.
 *
 * @returns {string} Uppercased ISO 3166-1 alpha-2 country code, e.g. `'US'`, or `''` if unavailable.
 */
function getCountryCode() {
  return window.Shopify?.country?.toUpperCase() || '';
}

/**
 * Returns the GWP promotion config object from `window.tartePromotionGwpConfig`,
 * which is set by `theme.liquid` from the shop metafield.
 *
 * @returns {Object|null} The config object, or `null` if absent or invalid.
 */
function getGwpConfig() {
  const config = window.tartePromotionGwpConfig;
  if (!config || typeof config !== 'object') return null;
  return config;
}

/**
 * Returns `false` if dev mode or testing mode gates block the current visitor.
 * Evaluated first, before all other eligibility checks.
 *
 * - Dev mode: hides the promotion on the published (`main`) theme.
 * - Testing mode: restricts the promotion to customers with at least one of the
 *   configured `testingTags`.
 *
 * @param {Object} discountEntry - A single discount entry from the GWP config.
 * @returns {boolean} `true` if the visitor passes all mode gates, `false` otherwise.
 */
function isTestingEligible(discountEntry) {
  if (discountEntry.devMode) {
    if (window.theme?.role === 'main') return false;
  }
  if (discountEntry.testingMode) {
    const customerTags = window.customerTags || [];
    if (!customerTags.includes('QATest')) return false;
  }
  return true;
}

/**
 * Returns `true` if the current customer matches the `applicableCustomers`
 * restriction of the given promo mode config.
 *
 * Supported values: `'all'` | `'loyalty'` (logged-in) | `'non-loyalty'` (guest) |
 * `'new-customers'` (logged-in with zero prior orders).
 *
 * @param {Object} modeConfig - The `autoAdd` or `selectFromMultiple` config block.
 * @returns {boolean}
 */
function isCustomerEligible(modeConfig) {
  const applicableCustomers = modeConfig?.applicableCustomers;
  if (!applicableCustomers || applicableCustomers === 'all') return true;
  const isLoggedIn = window.customerIsLoggedIn === true;
  if (applicableCustomers === 'loyalty') return isLoggedIn;
  if (applicableCustomers === 'non-loyalty') return !isLoggedIn;
  if (applicableCustomers === 'new-customers') {
    return isLoggedIn && window.customerOrderCount === 0;
  }
  return true;
}

/**
 * Returns `true` if the cart meets the spend/quantity/product threshold defined
 * in a single promo mode config, accounting for locale overrides.
 *
 * Threshold types:
 * - `'amount-threshold'`   — cart subtotal (dollars) ≥ value
 * - `'quantity-threshold'` — non-GWP item count ≥ value
 * - `'products-threshold'` — at least one qualifying product ID is in the cart
 *
 * @param {Object} modeConfig                            - The `autoAdd` or `selectFromMultiple` config block.
 * @param {number} cartTotal                             - Non-GWP cart subtotal in dollars.
 * @param {number} totalQty                              - Non-GWP item count.
 * @param {Array}  [regularItems=[]]                     - Non-GWP, non-gift-card cart line items.
 * @param {Array}  [productsThresholdItems=regularItems] - Items eligible for products-threshold
 *                                                         (includes gift cards, excludes GWP/samples/donations).
 * @returns {boolean}
 */
function isPromoEligible(modeConfig, cartTotal, totalQty, regularItems = [], productsThresholdItems = regularItems) {
  if (!modeConfig?.enabled) return false;

  const countryCode = getCountryCode();

  const excludedLocales = modeConfig.excludedLocales || [];
  if (excludedLocales.includes(countryCode)) {
    return false;
  }

  const localeThresholds = modeConfig.localeThresholds || [];
  const localeOverride = localeThresholds.find(
    (lt) => Array.isArray(lt.countryCodes) && lt.countryCodes.includes(countryCode)
  );

  const effectiveThresholdType = localeOverride?.thresholdType ?? modeConfig.selectedQualifier ?? modeConfig.thresholdType;
  const effectiveThresholdValue = localeOverride?.value ?? (
    effectiveThresholdType === 'amount-threshold'
      ? modeConfig.thresholdAmountValue
      : modeConfig.thresholdQuantityValue
  );
  const thresholdValue = Number(effectiveThresholdValue) || 0;

  if (effectiveThresholdType === 'amount-threshold') {
    return cartTotal >= thresholdValue;
  }

  if (effectiveThresholdType === 'quantity-threshold') {
    return totalQty >= thresholdValue;
  }

  if (effectiveThresholdType === 'products-threshold') {
    const qualifyingIds = (localeOverride?.qualifyingProductIds || modeConfig.qualifyingProductIds || [])
      .map(extractNumericId)
      .filter(Boolean);
    if (!qualifyingIds.length) return false;
    return productsThresholdItems.some((item) => qualifyingIds.includes(String(item.product_id)));
  }

  return false;
}

/**
 * Returns `true` if a discount entry is currently active.
 *
 * An entry is active when all of the following are true:
 * - `enableDiscount` is explicitly `true`
 * - The current time is within `startDateTime`–`endDateTime` (if set)
 * - If `promotionCode` is set, the code is applied to the cart and `applicable !== false`
 *
 * @param {Object} discountEntry - A single discount entry from the GWP config.
 * @param {Object} cart          - The current Shopify cart object from `/cart.js`.
 * @returns {boolean}
 */
function isDiscountActive(discountEntry, cart) {
  if (discountEntry.enableDiscount !== true) return false;

  const now = Date.now();
  if (discountEntry.startDateTime && now < new Date(discountEntry.startDateTime).getTime()) return false;
  if (discountEntry.endDateTime && now > new Date(discountEntry.endDateTime).getTime()) return false;

  if (discountEntry.promotionCode) {
    const match = (cart?.discount_codes || []).find(
      (d) => d.code.toLowerCase() === discountEntry.promotionCode.toLowerCase()
    );
    if (!match || match.applicable === false) return false;
  }

  return true;
}

/**
 * Evaluates GWP eligibility for all active promotions against the current cart.
 *
 * @param {Object} cart - The Shopify cart object from `/cart.js`.
 * @returns {{ autoAdd: Array<{eligible: boolean, config: Object}>, selectFromMultiple: Array<{eligible: boolean, config: Object}> }}
 *   Eligibility results for each active promo mode, keyed by type.
 */
function checkGwpEligibility(cart) {
  const config = getGwpConfig();

  if (!config) {
    return { autoAdd: [], selectFromMultiple: [] };
  }

  // Separate GWP lines from regular lines.
  // productsThresholdItems includes gift cards — they count as qualifying items for products-threshold.
  // regularItems further excludes gift cards for amount/quantity threshold calculations.
  const productsThresholdItems = (cart.items || []).filter((item) => {
    const isGwp = item.properties?._tarte_promotion === 'free_gwp';
    const isSample = String(item.properties?._freesample ?? '').toLowerCase() === 'true';
    const isDonation = item.product_type === 'Donation';
    const isRegiosAutoAdded = !!item.properties?._regios_auto_add_to_cart_node_id;
    const isLoyaltyDiscount = !!item.properties?._yotpo_loyalty_discount_type;
    return !isGwp && !isSample && !isDonation && !isRegiosAutoAdded && !isLoyaltyDiscount;
  });
  const regularItems = productsThresholdItems.filter(
    (item) => item.gift_card !== true && item.product_type !== 'Gift Card'
  );

  // Prices in /cart.js are in cents — convert to dollars for threshold comparison.
  // line_price already reflects all discounts (automatic + cart-level discount codes).
  const cartTotal = regularItems.reduce(
    (sum, item) => sum + (item.line_price / 100), 0
  );

  const totalQty = regularItems.reduce((sum, item) => sum + item.quantity, 0);
  const autoAddResults = [];
  const selectFromMultipleResults = [];

  for (const discountEntry of Object.values(config)) {
    if (!discountEntry || typeof discountEntry !== 'object') continue;
    if (!isDiscountActive(discountEntry, cart)) continue;
    if (!isTestingEligible(discountEntry)) {
      if (discountEntry.autoAdd?.enabled) {
        autoAddResults.push({ eligible: false, config: discountEntry.autoAdd });
      }
      if (discountEntry.selectFromMultiple?.enabled) {
        selectFromMultipleResults.push({ eligible: false, config: discountEntry.selectFromMultiple });
      }
      continue;
    }
    if (discountEntry.autoAdd) {
      autoAddResults.push({
        eligible: isCustomerEligible(discountEntry.autoAdd) && isPromoEligible(discountEntry.autoAdd, cartTotal, totalQty, regularItems, productsThresholdItems),
        config: discountEntry.autoAdd,
      });
    }
    if (discountEntry.selectFromMultiple) {
      selectFromMultipleResults.push({
        eligible: isCustomerEligible(discountEntry.selectFromMultiple) && isPromoEligible(discountEntry.selectFromMultiple, cartTotal, totalQty, regularItems, productsThresholdItems),
        config: discountEntry.selectFromMultiple,
      });
    }
  }

  return { autoAdd: autoAddResults, selectFromMultiple: selectFromMultipleResults };
}

/**
 * Returns the standard headers required for Shopify cart AJAX requests.
 *
 * @returns {{ 'Content-Type': string, Accept: string, 'X-Requested-With': string }}
 */
function getCartFetchHeaders() {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/javascript',
    'X-Requested-With': 'XMLHttpRequest',
  };
}

/**
 * Passes a cart API response to the active cart UI element (`cart-drawer` or
 * `cart-notification`) to re-render its contents without opening the drawer.
 *
 * @param {Object} json - The response body from a Shopify cart add/change request.
 * @returns {void}
 */
function renderCartContents(json) {
  const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  if (!cartEl) return;

  if (cartEl.tagName.toLowerCase() === 'cart-drawer') {
    cartEl.renderContents(json, false, false);
  } else {
    cartEl.renderContents(json);
  }
}

/**
 * Adds the auto-add GWP variant to the cart, subject to dismissal and duplicate guards.
 *
 * Guards (checked in order):
 * 1. sessionStorage dismissed set — fast, same-tab check.
 * 2. Cart attribute `_gwp_auto_add_dismissed` — catches cross-tab and post-reload dismissals.
 * 3. Fresh `/cart.js` check — prevents race-condition double-adds.
 *
 * @param {string} variantGid - Shopify GID of the variant to add, e.g. `gid://shopify/ProductVariant/123`.
 * @returns {Promise<boolean>} `true` if the variant was successfully added, `false` if skipped or already present.
 */
async function addGwpAutoAddVariant(variantGid) {
  const numericId = extractNumericId(variantGid);
  if (getDismissedAutoAddVariants().has(numericId)) return false;

  const checkRes = await fetch('/cart.js');
  const freshCart = await checkRes.json();

  const cartDismissedRaw = freshCart.attributes?._gwp_auto_add_dismissed;
  if (cartDismissedRaw) {
    try {
      const cartDismissed = JSON.parse(cartDismissedRaw);
      if (Array.isArray(cartDismissed) && cartDismissed.includes(numericId)) return false;
    } catch (_) {}
  }

  const alreadyInCart = freshCart.items.some(
    (item) =>
      String(item.variant_id) === numericId &&
      item.properties?._tarte_promotion === 'free_gwp' &&
      item.properties?._tarte_gwp_type === 'auto_add',
  );
  if (alreadyInCart) return false;

  const country = getCountryCode();
  const url = `${window.routes.cart_add_url}?country=${encodeURIComponent(country)}`;

  const res = await fetch(url, {
    method: 'POST',
    headers: getCartFetchHeaders(),
    body: JSON.stringify({
      id: numericId,
      quantity: 1,
      properties: { _tarte_promotion: 'free_gwp', _tarte_gwp_type: 'auto_add' },
      sections: 'cart-icon-bubble,cart-drawer',
    }),
  });

  const json = await res.json();
  renderCartContents(json);
  return true;
}

/**
 * Removes the auto-add GWP variant from the cart if it is present.
 * No-ops if the variant is not found in the provided cart snapshot.
 *
 * @param {string} variantGid - Shopify GID of the variant to remove.
 * @param {Object} cart       - The current Shopify cart object from `/cart.js`.
 * @returns {Promise<void>}
 */
async function removeGwpAutoAddVariant(variantGid, cart) {
  const numericId = extractNumericId(variantGid);
  const gwpItem = cart.items.find(
    (item) =>
      String(item.variant_id) === numericId &&
      item.properties?._tarte_promotion === 'free_gwp' &&
      item.properties?._tarte_gwp_type === 'auto_add',
  );
  if (!gwpItem) return;

  const country = getCountryCode();
  const url = `${window.routes.cart_change_url}?country=${encodeURIComponent(country)}`;

  const res = await fetch(url, {
    method: 'POST',
    headers: getCartFetchHeaders(),
    body: JSON.stringify({
      id: gwpItem.key,
      quantity: 0,
      sections: 'cart-icon-bubble,cart-drawer',
    }),
  });

  const json = await res.json();
  renderCartContents(json);
}

/**
 * Removes select-from-multiple (SFM) GWP cart lines that belong to the given
 * SFM promo config, respecting skip-lists that protect items owned by other promos.
 *
 * @param {Object}      sfmConfig                   - The `selectFromMultiple` config block.
 * @param {Object}      cart                         - The current Shopify cart object from `/cart.js`.
 * @param {Set<string>} [skipVariantIds=new Set()]   - Numeric variant IDs to protect from removal
 *                                                     (auto-add variants owned by eligible promos).
 * @param {Set<string>} [skipProductIds=new Set()]   - Numeric product IDs to protect from removal
 *                                                     (products claimed by any active SFM promo).
 * @returns {Promise<void>}
 */
async function removeGwpSelectFromMultipleVariants(sfmConfig, cart, skipVariantIds = new Set(), skipProductIds = new Set()) {
  const selectedIds = (sfmConfig.selectedProductIds || []).map(extractNumericId).filter(Boolean);
  if (!selectedIds.length) return;

  const gwpItemsToRemove = cart.items.filter(
    (item) =>
      item.properties?._tarte_promotion === 'free_gwp' &&
      selectedIds.includes(String(item.product_id)) &&
      !skipProductIds.has(String(item.product_id)) &&
      !(skipVariantIds.has(String(item.variant_id)) && item.properties?._tarte_gwp_type === 'auto_add')
  );
  if (!gwpItemsToRemove.length) return;

  const country = getCountryCode();
  const url = `${window.routes.cart_change_url}?country=${encodeURIComponent(country)}`;

  for (const item of gwpItemsToRemove) {
    const res = await fetch(url, {
      method: 'POST',
      headers: getCartFetchHeaders(),
      body: JSON.stringify({
        id: item.key,
        quantity: 0,
        sections: 'cart-icon-bubble,cart-drawer',
      }),
    });
    const json = await res.json();
    renderCartContents(json);
  }
}


const autoAddVariantsEligibleInCart = new Set();

// Guard against overlapping calls from rapid cart events
let isUpdatingGwp = false;

/**
 * Fetches the current cart, runs eligibility checks for all active GWP promotions,
 * reconciles auto-add and select-from-multiple cart lines, and dispatches
 * `tarte:gwp-eligibility-updated` with the result.
 *
 * Re-entrant calls are debounced via `isUpdatingGwp` to prevent race conditions
 * from rapid cart events.
 *
 * @returns {Promise<void>}
 */
async function updateGwpEligibility() {
  if (isUpdatingGwp) return;
  isUpdatingGwp = true;

  try {
    const res = await fetch('/cart.js');
    if (!res.ok) return;
    const cart = await res.json();

    const eligibility = checkGwpEligibility(cart);

    const autoAddVariantIds = new Set(
      eligibility.autoAdd
        .filter(({ eligible }) => eligible)
        .map(({ config: c }) => extractNumericId(c?.selectedProductIds?.[0]))
        .filter(Boolean)
    );

    // Snapshot: which eligible auto-add variants are in the cart RIGHT NOW?
    // Compared against autoAddVariantsEligibleInCart (previous run's snapshot) to detect
    // user-initiated removal without relying on the click handler.
    const nextEligibleInCart = new Set();
    for (const { eligible, config: c } of eligibility.autoAdd) {
      if (!eligible) continue;
      const id = extractNumericId(c?.selectedProductIds?.[0]);
      if (!id) continue;
      if (cart.items.some((item) => String(item.variant_id) === id && item.properties?._tarte_promotion === 'free_gwp')) {
        nextEligibleInCart.add(id);
      }
    }

    for (const { eligible, config: autoAddConfig } of eligibility.autoAdd) {
      const variantGid = autoAddConfig?.selectedProductIds?.[0];
      if (!variantGid) continue;
      const numericId = extractNumericId(variantGid);
      const gwpItem = cart.items.find(
        (item) => String(item.variant_id) === numericId && item.properties?._tarte_promotion === 'free_gwp'
      );
      const gwpInCart = !!gwpItem;

      if (eligible && gwpInCart) {
        nextEligibleInCart.add(numericId);
      } else if (eligible && !gwpInCart) {
        if (autoAddVariantsEligibleInCart.has(numericId)) {
          dismissAutoAddVariant(numericId);
        } else {
          const added = await addGwpAutoAddVariant(variantGid);
          if (added) nextEligibleInCart.add(numericId);
        }
      } else if (!eligible && gwpInCart && !autoAddVariantIds.has(numericId)) {
        await removeGwpAutoAddVariant(variantGid, cart);
      }
    }

    autoAddVariantsEligibleInCart.clear();
    for (const id of nextEligibleInCart) autoAddVariantsEligibleInCart.add(id);

    for (const { eligible, config: sfmConfig } of eligibility.selectFromMultiple) {
      if (!eligible) {
        await removeGwpSelectFromMultipleVariants(sfmConfig, cart, autoAddVariantIds);
      } else {
        // Eligible but Shopify Function stripped the discount — remove affected items.
        const selectedIds = (sfmConfig.selectedProductIds || []).map(extractNumericId).filter(Boolean);
        const discountMissingItems = cart.items.filter(
          (item) =>
            item.properties?._tarte_promotion === 'free_gwp' &&
            selectedIds.includes(String(item.product_id)) &&
            item.final_line_price > 0 &&
            !autoAddVariantIds.has(String(item.variant_id))
        );
        for (const item of discountMissingItems) {
          await removeGwpSelectFromMultipleVariants(sfmConfig, { items: [item] }, autoAddVariantIds);
        }
      }
    }

    const fullConfig = getGwpConfig();
    if (fullConfig) {
      const activeSfmProductIds = new Set();
      for (const entry of Object.values(fullConfig)) {
        if (!entry || typeof entry !== 'object') continue;
        if (!isDiscountActive(entry, cart)) continue;
        if (entry.selectFromMultiple?.enabled) {
          for (const id of (entry.selectFromMultiple.selectedProductIds || [])) {
            activeSfmProductIds.add(extractNumericId(id));
          }
        }
      }

      for (const discountEntry of Object.values(fullConfig)) {
        if (!discountEntry || typeof discountEntry !== 'object') continue;
        if (isDiscountActive(discountEntry, cart)) continue; // active promos handled above

        if (discountEntry.autoAdd) {
          const variantGid = discountEntry.autoAdd?.selectedProductIds?.[0];
          if (variantGid) {
            const numericId = extractNumericId(variantGid);

            if (!autoAddVariantIds.has(numericId)) {
              await removeGwpAutoAddVariant(variantGid, cart);
            }
          }
        }
        if (discountEntry.selectFromMultiple) {
          await removeGwpSelectFromMultipleVariants(discountEntry.selectFromMultiple, cart, autoAddVariantIds, activeSfmProductIds);
        }
      }
    }

    document.dispatchEvent(new CustomEvent('tarte:gwp-eligibility-updated', {
      detail: eligibility,
    }));
  } catch (err) {
    console.error('[custom-promotions] Failed to update GWP eligibility:', err);
  } finally {
    isUpdatingGwp = false;
  }
}


window.checkGwpEligibility = checkGwpEligibility;
window.updateGwpEligibility = updateGwpEligibility;

document.addEventListener('tarte:cart-data-updated', updateGwpEligibility);
document.addEventListener('cartdrawer:open', updateGwpEligibility);
document.addEventListener('DOMContentLoaded', updateGwpEligibility);
document.addEventListener('customer:loggedin', updateGwpEligibility);

// Track manual removal of auto-add GWP items so they don't get re-added this session
document.addEventListener('click', function(e) {
  const removeBtn = e.target.closest('cart-remove-button');
  if (!removeBtn) return;
  const row = removeBtn.closest('tr[data-gwp-auto-add="true"]');
  if (!row) return;
  const variantId = row.dataset.variantId;
  if (variantId) dismissAutoAddVariant(variantId);
});

// Delegated click handler for free-gift add buttons.
// Runs unconditionally so it works even when free-gift.liquid renders after page load.
document.addEventListener('click', function(e) {
  const cta = e.target.closest('.free-gift-product__cta');
  if (!cta) return;
  const productHandle = cta.getAttribute('data-product-handle');
  const variantId = cta.getAttribute('data-variant-id');
  const loadQv = cta.classList.contains('btn--load-quickview');
  if (loadQv && productHandle) {
    window.loadQuickview({ url: `/products/${productHandle}`, source: 'tarte_gwp' });
  } else {
    window.addToCartBySku(variantId, 'tarte_gwp');
  }
});
